import { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import Modal from "../../../../../../components/Modal";
import Button from "../../../../../../components/Button";
import Spinner from "../../../../../../components/Loader";
import { Center } from "../../../../../../components/StyledComponents";
import Snackbar from "../../../../../../components/Snackbar";
import { Title, Text, SubscriptionInfoItem } from "../CancelMemberModal/styles";
import {
  editSubscription,
  getSubscription,
  resetEditSubscription,
  resetGetSubscription,
} from "../../../../../../store/actions/subscription-admin";
import { hideModal } from "../../../../../../store/actions/modal";
import { getGroupSubscribers } from "../../../../../../store/actions/group";
import { Group, Subscription } from "../../../../../../types";
import dateHelpers from "../../../../../../helpers/dates-hanlder";

interface ComponentProps {
  subscriptionId: number | null;
  subscription: Subscription | null;
  subscriptionStates: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  editSubscriptionStates: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  group: Group;
  setSelectedSubscriptionId: (subscriptionId: number | null) => void;
}

const CancelInvitationModal = ({
  subscriptionId,
  subscription,
  subscriptionStates,
  editSubscriptionStates,
  group,
  setSelectedSubscriptionId,
}: ComponentProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (subscriptionId) {
      dispatch(getSubscription(subscriptionId));
    }
  }, [dispatch, subscriptionId]);

  useEffect(() => {
    if (!editSubscriptionStates.success) {
      return undefined;
    }

    dispatch(getGroupSubscribers({ page: 1, id: group?.id, filters: [] }));
    dispatch(hideModal());
    setSelectedSubscriptionId(null);

    const timeout = setTimeout(() => {
      dispatch(resetEditSubscription());
    }, 3000);

    return () => clearTimeout(timeout);
  }, [dispatch, editSubscriptionStates.success, group?.id, setSelectedSubscriptionId]);

  const handleClose = () => {
    dispatch(hideModal());
  };

  const handleModalClose = () => {
    setSelectedSubscriptionId(null);
    dispatch(resetGetSubscription());
  };

  const handleConfirm = () => {
    if (!subscriptionId) {
      return;
    }

    dispatch(
      editSubscription({
        id: subscriptionId,
        status: "expired",
        expire_at: dateHelpers.setToZeroTimeZone(new Date()),
      })
    );
  };

  return (
    <>
      <Modal
        name="cancel-invitation-modal"
        width="560"
        showClose={true}
        onClose={handleModalClose}
      >
        {subscriptionStates.success ? (
          <>
            <Title>Cancelar invitación</Title>
            <Text>
              Esta acción marca la invitación como expirada y evitará que el
              invitado acceda con el enlace que recibió. ¿Deseas continuar?
            </Text>

            <SubscriptionInfoItem>
              Invitado: <i>{subscription?.subscriber?.email}</i>
            </SubscriptionInfoItem>

            <div style={{ height: 24 }} />

            <Center horizontal="center" vertical="center" gap={12}>
              <Button
                options={{
                  type: "outline",
                  skin: "purple",
                  size: "lg",
                  block: true,
                }}
                onClick={handleClose}
              >
                Volver
              </Button>

              <Button
                options={{
                  type: "filled",
                  skin: "purple",
                  size: "lg",
                  block: true,
                }}
                disabled={editSubscriptionStates.loading}
                onClick={handleConfirm}
              >
                <Center vertical="center" horizontal="center" gap={5}>
                  {editSubscriptionStates.loading ? (
                    <Spinner color="#fff" width={60} />
                  ) : (
                    <>Confirmar</>
                  )}
                </Center>
              </Button>
            </Center>
          </>
        ) : (
          <Center vertical="center" horizontal="center" column gap={12}>
            <p>
              {subscriptionStates.loading
                ? "Cargando invitación..."
                : "No se pudo cargar la invitación."}
            </p>
            {subscriptionStates.loading && <Spinner width={60} />}
          </Center>
        )}
      </Modal>

      <Snackbar
        visible={editSubscriptionStates.success || !!editSubscriptionStates.error}
        options={{
          time: 3000,
          position: "center",
          type: editSubscriptionStates.success ? "success" : "error",
        }}
        onHide={() => dispatch(resetEditSubscription())}
      >
        {editSubscriptionStates.success
          ? "Invitación cancelada exitosamente."
          : "No se pudo cancelar la invitación."}
      </Snackbar>
    </>
  );
};

const state = ({ subscriptionAdminStore, groupStore }) => {
  const { data: subscription, states: subscriptionStates } =
    subscriptionAdminStore.subscriptionAdmin;
  const { states: editSubscriptionStates } = subscriptionAdminStore.editSubscription;
  const { data: group } = groupStore.group;

  return {
    subscription,
    subscriptionStates,
    editSubscriptionStates,
    group,
  };
};

export default connect(state)(CancelInvitationModal);

